import { Component, inject } from '@angular/core';
import { TabComponent } from '../../../components/tab/tab.component';
import { ButtonDirective } from '../../../components/button/button.directive';
import { NgIconComponent } from '@ng-icons/core';
import { SnackbarService } from '../../../services/snackbar.service';
import * as XLSX from 'xlsx';
import { UserCategoriesService } from '../../../services/leave-management/user-categories.service';

export interface UserCategoryUpload {
  id: string;
  category: string;
}

interface ExcelRow {
  [index: number]: string;
}

@Component({
  selector: 'app-users-categories',
  imports: [ButtonDirective, NgIconComponent],
  providers: [{ provide: TabComponent, useExisting: UsersCategoriesComponent }],
  templateUrl: './users-categories.component.html',
  styleUrl: './users-categories.component.scss',
})
export class UsersCategoriesComponent extends TabComponent {
  override name: string = this.dictionaryService.get('UsersCategories');
  public async main(): Promise<void> {}

  private messageService = inject(SnackbarService);
  private usersCategoriesService = inject(UserCategoriesService);

  public categories: any[] = ['A', 'B', 'C'];

  public async onFileChange(evt: Event): Promise<void> {
    const target = evt.target as HTMLInputElement;

    if (!target.files || target.files.length !== 1) {
      this.messageService.showError('Please select a single file.');
      return;
    }

    const file = target.files[0];
    this.messageService.showInfo('Processing file...');

    try {
      // 1. Parse Excel to Data
      const rawData = await this.readExcelFile(file);

      // 2. Validate Data
      const validUsers = this.validateAndMapData(rawData);

      // 3. Upload & Update State
      this.usersCategoriesService.uploadFile(validUsers);
    } catch (error: any) {
      this.messageService.showError('File parsing failed');
      console.log(error.message);
      target.value = '';
    }
  }

  public downloadTemplate(): void {
    this.usersCategoriesService.downloadTemplate().subscribe({
      next: (url) => {
        window.open(url, '_blank');
      },
      error: (err) => {
        console.log(err.message);
      },
    });
  }

  /**
   * Helper: Promisify the FileReader for cleaner async/await usage
   * Reads only the first sheet by default.
   */
  private readExcelFile(file: File): Promise<ExcelRow[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        try {
          const arrayBuffer = e.target.result;
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          const jsonData = XLSX.utils.sheet_to_json<ExcelRow>(worksheet, {
            header: 1,
          });
          resolve(jsonData);
        } catch (err) {
          reject(new Error('Invalid Excel file format'));
        }
      };

      reader.onerror = () => reject(new Error('Error reading file'));

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Helper: Validates raw Excel rows and maps them to strict objects.
   * Throws an error immediately if bad data is found.
   */
  private validateAndMapData(rows: ExcelRow[]): UserCategoryUpload[] {
    if (rows.length < 2) {
      throw new Error('File is empty or missing headers');
    }

    // Remove Header Row
    const dataRows = rows.slice(1);
    const validData: UserCategoryUpload[] = [];

    // Map & Validate
    dataRows.forEach((row, index) => {
      const userId = row[0]; // Column A
      const category = row[2]; // Column C

      if (!userId && !category) return;

      const realRowNumber = index + 2; // +1 for header, +1 for 0-index

      if (!this.isObjectId(userId)) {
        throw new Error(`Row ${realRowNumber}: Invalid User ID format.`);
      }

      if (!this.categories.includes(category)) {
        throw new Error(
          `Row ${realRowNumber}: Unknown Category '${category}'.`,
        );
      }

      validData.push({ id: userId, category });
    });

    if (validData.length === 0) {
      throw new Error('No valid data found in file.');
    }

    return validData;
  }

  /**
   * Utility: Validates data
   */
  private isObjectId(str: string): boolean {
    return /^[0-9a-fA-F]{24}$/.test(str || '');
  }
}
